import { motion } from "framer-motion";
import { Leaf, Mail } from "lucide-react";
import { SEO } from "@/components/nyla/SEO";

const sections = [
  {
    title: "1. Responsible water use",
    intro: "We will work to conserve water throughout our operations by:",
    bullets: [
      "Monitoring water used in production, bottle washing, rinsing, cleaning and other plant activities.",
      "Identifying and addressing leaks, avoidable losses and excessive consumption.",
      "Improving cleaning and rinsing processes while maintaining hygiene and product quality.",
      "Evaluating opportunities to recover or reuse water where technically suitable, permitted and safe.",
      "Managing wastewater responsibly to prevent contamination of soil, drains and surrounding water sources.",
    ],
    outro: "Water conservation measures will always account for food safety and the suitability of water for its intended use.",
  },
  {
    title: "2. Responsible packaging and bottle reuse",
    intro:
      "We will support the return, inspection and safe reuse of suitable glass bottles through our collection arrangements with customers and partners. We commit to:",
    bullets: [
      "Encouraging customers to return eligible bottles and reusable crates.",
      "Inspecting returned bottles and removing those that are damaged, deteriorated or unsuitable for further use.",
      "Establishing and reviewing washing, sanitisation and rejection criteria.",
      "Reusing bottles only when they meet our quality and safety requirements.",
      "Sending rejected glass and other recyclable packaging to appropriate recycling channels.",
      "Reducing unnecessary packaging and evaluating packaging choices for durability, material use, transport requirements and end-of-life recovery.",
    ],
    outro: "We will assess the environmental impact of reuse alongside the water, energy and chemicals required for washing and collection.",
  },
  {
    title: "3. Energy efficiency and climate responsibility",
    intro:
      "We recognise that atmospheric water generation and bottling require electricity. We will work to improve energy efficiency through equipment selection, preventive maintenance and effective operating practices. Our priorities include:",
    bullets: [
      "Tracking electricity consumption in relation to production.",
      "Reducing avoidable equipment idling and energy losses.",
      "Maintaining cooling equipment and managing refrigerants responsibly.",
      "Evaluating renewable electricity options where practical and commercially feasible.",
      "Developing a better understanding of emissions associated with energy, packaging and transportation.",
    ],
    outro:
      "Any future emissions-reduction or carbon-neutrality targets will be supported by a defined scope, baseline, measurement method and implementation plan.",
  },
  {
    title: "4. Waste reduction and pollution prevention",
    intro: "We will apply the principles of reducing waste, reusing suitable materials and recycling wherever practical. We will:",
    bullets: [
      "Segregate glass, plastic, paper, cardboard, metal and other waste at appropriate collection points.",
      "Store waste safely and use authorised handlers where required.",
      "Handle cleaning chemicals, used oils, electronic waste and other controlled waste according to applicable requirements.",
      "Maintain procedures for chemical storage, dosing, spill prevention and spill response.",
      "Manage wastewater and discharges through appropriate treatment or disposal arrangements.",
      "Investigate environmental incidents and take corrective action.",
    ],
  },
  {
    title: "5. Responsible sourcing and distribution",
    paragraphs: [
      "We will consider environmental performance alongside quality, safety, reliability and cost when selecting suppliers and service providers.",
      "We will encourage partners to minimise unnecessary packaging, manage waste responsibly and provide accurate information about materials and environmental claims.",
      "For distribution, we will seek opportunities to improve route planning, consolidate deliveries and coordinate bottle collections with deliveries. We will maintain vehicles appropriately and review avoidable fuel use and unnecessary trips.",
    ],
  },
  {
    title: "6. Compliance and employee participation",
    paragraphs: [
      "We commit to meeting applicable environmental requirements, permit conditions and environmental obligations we formally accept.",
      "Management will assign responsibilities for implementation and provide relevant guidance to employees and contractors. Team members will be encouraged to report leaks, spills, waste-handling concerns and opportunities for improvement.",
      "Environmental concerns will be reviewed promptly, with actions assigned to the responsible team.",
    ],
  },
  {
    title: "7. Measurement and continual improvement",
    intro: "We will establish baseline information and measurable improvement objectives appropriate to our operations. Where relevant, we will track:",
    bullets: [
      "Electricity consumption per litre of finished product.",
      "Total operational water use per litre of finished product.",
      "Bottle return, reuse and rejection rates.",
      "Packaging consumption and waste sent for recycling.",
      "Delivery fuel consumption or distance travelled.",
      "Environmental incidents and completion of corrective actions.",
    ],
    outro: "Management will review progress at least annually, agree priorities and update objectives based on findings.",
  },
  {
    title: "8. Transparent communication and policy review",
    paragraphs: [
      "We will ensure that environmental statements are clear, specific and supported by evidence. Any published certification, rating, environmental achievement or target will accurately describe its scope and status.",
      "The CEO & Managing Director is responsible for overseeing this policy. We will review it annually, or earlier when significant operational or regulatory changes occur.",
      "This policy will be publicly available on our website and communicated to relevant employees and business partners.",
    ],
  },
];

export default function SustainabilityPolicy() {
  return (
    <div data-testid="sustainability-policy-page" className="pt-14">
      <SEO
        title="Environmental Sustainability Policy"
        description="The Environmental Sustainability Policy of Jaitra Wellness Private Limited (Nyla Air Water) — responsible water use, packaging reuse, energy efficiency, waste reduction and transparent communication."
        path="/sustainability-policy"
        keywords="Nyla sustainability policy, environmental policy, Jaitra Wellness, glass bottle reuse, responsible water use"
      />

      <section className="py-20 md:py-28 bg-[#F5F5F7]">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
            <div className="w-12 h-12 rounded-2xl bg-[#2C3E35]/10 flex items-center justify-center mb-6">
              <Leaf className="w-6 h-6 text-[#2C3E35]" strokeWidth={1.6} />
            </div>
            <p className="text-xs md:text-sm tracking-[0.2em] uppercase font-semibold text-[#86868B] mb-6">
              Jaitra Wellness Private Limited · Nyla Air Water
            </p>
            <h1 data-testid="policy-heading" className="font-display text-4xl md:text-5xl lg:text-6xl leading-tight tracking-tight font-bold text-[#1D1D1F]">
              Environmental Sustainability Policy.
            </h1>
            <div className="mt-8 flex flex-wrap gap-x-8 gap-y-2 text-sm text-[#86868B]">
              <p><span className="font-semibold text-[#1D1D1F]">Effective date:</span> 06 July 2026</p>
              <p><span className="font-semibold text-[#1D1D1F]">Policy owner:</span> CEO &amp; Managing Director</p>
              <p><span className="font-semibold text-[#1D1D1F]">Version:</span> 1.2</p>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.7 }}
            className="mb-14"
          >
            <h2 className="font-display text-2xl md:text-3xl tracking-tight font-bold text-[#1D1D1F] mb-5">Our commitment</h2>
            <div className="space-y-4 text-base leading-relaxed text-[#1D1D1F]/75">
              <p>
                At Jaitra Wellness Private Limited, the company behind Nyla Air Water, we are committed to
                protecting the environment and improving the way we use water, energy and materials.
              </p>
              <p>
                Our water-from-air approach offers an alternative source of drinking water. We recognise that
                responsible production also requires careful management of electricity, packaging, washing water,
                chemicals, transportation and waste.
              </p>
              <p>
                This policy guides our decisions across manufacturing, procurement, packaging, storage,
                distribution and office activities. We will communicate relevant expectations to employees,
                suppliers, contractors and distribution partners.
              </p>
            </div>
          </motion.div>

          <div className="space-y-12">
            {sections.map((s) => (
              <motion.div
                key={s.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.6 }}
              >
                <h2 className="font-display text-xl md:text-2xl tracking-tight font-bold text-[#1D1D1F] mb-4">{s.title}</h2>
                {s.intro && <p className="text-base leading-relaxed text-[#1D1D1F]/75 mb-4">{s.intro}</p>}
                {s.paragraphs?.map((p, i) => (
                  <p key={i} className="text-base leading-relaxed text-[#1D1D1F]/75 mb-4">{p}</p>
                ))}
                {s.bullets && (
                  <ul className="space-y-2.5 mb-4">
                    {s.bullets.map((b) => (
                      <li key={b} className="flex items-start gap-3 text-base leading-relaxed text-[#1D1D1F]/75">
                        <span className="w-1.5 h-1.5 rounded-full bg-[#2C3E35] mt-2.5 shrink-0" />
                        {b}
                      </li>
                    ))}
                  </ul>
                )}
                {s.outro && <p className="text-sm leading-relaxed text-[#86868B] italic">{s.outro}</p>}
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-40px" }}
            transition={{ duration: 0.6 }}
            className="mt-16 rounded-3xl bg-[#F5F5F7] p-8 md:p-10"
          >
            <h2 className="font-display text-xl md:text-2xl tracking-tight font-bold text-[#1D1D1F] mb-3">Contact</h2>
            <p className="text-base leading-relaxed text-[#1D1D1F]/75 mb-4">
              For questions, suggestions or environmental concerns, please contact:
            </p>
            <p className="text-base font-medium text-[#1D1D1F]">Jaitra Wellness Private Limited — Nyla Air Water</p>
            <a
              href="mailto:contactus@nylaairwater.earth"
              data-testid="policy-contact-email"
              className="mt-2 inline-flex items-center gap-2 text-sm font-medium text-[#2C3E35] hover:underline"
            >
              <Mail className="w-4 h-4" /> contactus@nylaairwater.earth
            </a>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
